import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArticleGenerationRow,
  ArticleMode,
  GenerationStage,
} from '../../types/articleGeneration';
import { GenerationProgress, progressFraction } from './progressReducer';
import ProgressRing from './ProgressRing';
import MaterialIcon from '../shared/MaterialIcon';

interface Props {
  mode: ArticleMode;
  progress: GenerationProgress;
  elapsedMs: number;
  runs: ArticleGenerationRow[];
  onCancel: () => void;
  // 'inline' renders as a plain card (for a polled/reconnected run) instead of
  // the modal overlay, and hides the Cancel button.
  variant?: 'modal' | 'inline';
  // Inline only: stop a run that continues server-side (sets it cancelled; the
  // pipeline honours that at its next checkpoint).
  onStop?: () => void;
}

// Mirror of the backend critique gate (critique.ts / handler.ts).
const CRITIQUE_MAX_ROUNDS = 3;
const CRITIQUE_PASS_THRESHOLD = 4;

const STAGES_BY_MODE: Record<ArticleMode, GenerationStage[]> = {
  full_article: ['structure', 'briefs', 'plan_review', 'writing', 'article_critique', 'language_polish'],
  headings: ['structure', 'plan_review', 'language_polish'],
  headings_briefs: ['structure', 'briefs', 'plan_review', 'language_polish'],
};

const formatClock = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const GenerationStatusPanel: React.FC<Props> = ({
  mode,
  progress,
  elapsedMs,
  runs,
  onCancel,
  variant = 'modal',
  onStop,
}) => {
  const { t } = useTranslation();
  const inline = variant === 'inline';
  const stages = STAGES_BY_MODE[mode];
  const currentIndex = progress.stage ? stages.indexOf(progress.stage) : stages.length;

  // The in-progress run isn't in `runs` until it finishes, so derive its real
  // version index: its position if already listed, otherwise the next number.
  const activeIndex = runs.findIndex((r) => r.id === progress.generationId);
  const versionNum = activeIndex >= 0 ? activeIndex + 1 : runs.length + 1;

  const stageState = (stage: GenerationStage) =>
    progress.stagesDone.includes(stage) ? 'done' : progress.stage === stage ? 'running' : 'waiting';

  const latestLine = progress.retry
    ? t('article.status_retrying', {
        attempt: progress.retry.attempt,
        max: progress.retry.maxAttempts,
      })
    : progress.stage === 'writing'
      ? t('article.status_writing_section', {
          heading: progress.latestHeading || t('article.lead_section'),
        })
      : progress.stage
        ? t(`article.stage_${progress.stage}`)
        : '';

  const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    inline ? (
      <div className="border rounded-lg bg-white p-6">{children}</div>
    ) : (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">{children}</div>
      </div>
    );

  return (
    <Shell>
      <>
        <div className="flex items-center gap-6">
          <ProgressRing fraction={progressFraction(progress, mode)} centerText={formatClock(elapsedMs)} />
          <div className="flex-grow">
            <div className="font-semibold mb-1">
              {t('article.version')} {versionNum}
            </div>
            <div className="text-sm text-gray-600">
              {t('article.stage_label', {
                current: Math.min(currentIndex + 1, stages.length),
                total: stages.length,
              })}
              {progress.stage === 'writing' && ` · ${progress.section + 1}/${progress.totalSections}`}
            </div>
            {progress.writerModel && (
              <div className="text-xs text-gray-400 mt-0.5 truncate">
                {t('article.writer_model_label', { model: progress.writerModel })}
              </div>
            )}
          </div>
        </div>

        <ul className="mt-4 space-y-2">
          {stages.map((stage) => {
            const state = stageState(stage);
            return (
              <li key={stage} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <MaterialIcon
                    name={
                      state === 'done'
                        ? 'check_circle'
                        : state === 'running'
                          ? 'progress_activity'
                          : 'radio_button_unchecked'
                    }
                    className={`text-base ${
                      state === 'done'
                        ? 'text-green-600'
                        : state === 'running'
                          ? 'text-blue-600 animate-spin'
                          : 'text-gray-400'
                    }`}
                  />
                  {t(`article.stage_${stage}`)}
                </span>
                <span className="text-gray-500 font-mono">
                  {stage === 'writing' && state === 'running'
                    ? `${progress.section + 1}/${progress.totalSections}`
                    : (stage === 'skeleton_critique' || stage === 'article_critique') &&
                        state === 'running' &&
                        progress.critique
                      ? t('article.critique_round', {
                          round: progress.critique.round,
                          positive: progress.critique.positive,
                          total: progress.critique.total,
                        })
                      : progress.stageDurations[stage] !== undefined
                        ? formatClock(progress.stageDurations[stage]!)
                        : ''}
                </span>
              </li>
            );
          })}
        </ul>

        {latestLine && <div className="mt-4 text-sm text-gray-700 truncate">{latestLine}</div>}

        {progress.critique && !progress.critiqueSkipped && progress.critique.total > 0 && (
          <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm">
            <div className="font-semibold text-blue-900">
              {t('article.critique_progress_title', {
                round: progress.critique.round,
                max: CRITIQUE_MAX_ROUNDS,
              })}
            </div>
            <div className="mt-1 text-blue-800">
              {t('article.critique_progress_detail', {
                positive: progress.critique.positive,
                total: progress.critique.total,
                threshold: CRITIQUE_PASS_THRESHOLD,
              })}
            </div>
          </div>
        )}

        {progress.critics.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-semibold text-gray-600 mb-1">
              {t('article.critics_live_title', {
                approved: progress.critics.filter((c) => c.ok && c.verdict === 'positive').length,
                rejected: progress.critics.filter((c) => c.ok && c.verdict === 'negative').length,
                failed: progress.critics.filter((c) => c.state === 'received' && c.ok === false)
                  .length,
                responded: progress.critics.filter((c) => c.state === 'received').length,
                total: progress.critics.length,
              })}
            </div>
            <ul className="space-y-1">
              {progress.critics.map((c) => (
                <li key={c.slot} className="flex items-center gap-2 text-xs">
                  <MaterialIcon
                    name={
                      c.state === 'sent'
                        ? 'progress_activity'
                        : c.ok === false
                          ? 'error'
                          : c.verdict === 'positive'
                            ? 'thumb_up'
                            : 'thumb_down'
                    }
                    className={`text-sm ${
                      c.state === 'sent'
                        ? 'text-blue-500 animate-spin'
                        : c.ok === false
                          ? 'text-red-500'
                          : c.verdict === 'positive'
                            ? 'text-green-600'
                            : 'text-amber-600'
                    }`}
                  />
                  <span className="text-gray-700">{c.label}</span>
                  <span className="font-mono text-gray-400 truncate">{c.model}</span>
                  <span
                    className={`ml-auto ${
                      c.state === 'received' && c.ok === false
                        ? 'text-red-500'
                        : 'text-gray-400'
                    }`}
                  >
                    {t(
                      c.state === 'sent'
                        ? 'article.critic_state_sent'
                        : c.ok === false
                          ? 'article.critic_state_error'
                          : 'article.critic_state_received'
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {progress.critiqueSkipped && (
          <div className="mt-2 text-xs text-gray-500">
            {t(
              progress.critiqueSkippedReason === 'critique_error'
                ? 'article.critique_skipped_error'
                : 'article.critique_skipped'
            )}
          </div>
        )}

        {inline ? (
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <span>{t('article.status_keeps_running')}</span>
              {progress.hop != null && progress.hop > 0 && (
                <span className="font-medium text-gray-600">
                  {t('article.status_part', { hop: progress.hop })}
                </span>
              )}
            </div>
            {onStop && (
              <button
                type="button"
                onClick={onStop}
                className="px-3 py-1.5 border border-red-300 text-red-600 rounded-md hover:bg-red-50 text-sm whitespace-nowrap"
              >
                {t('article.stop_run')}
              </button>
            )}
          </div>
        ) : (
          <div className="mt-6 flex items-center justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 text-sm"
            >
              {t('article.cancel_generation')}
            </button>
          </div>
        )}
      </>
    </Shell>
  );
};

export default GenerationStatusPanel;
